import React, { useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { del, get, post } from "../lib/api";
import { useDocumentTitle, useLoad, listUrl } from "../lib/hooks";
import { LINKEDIN_STATUSES, RELATIONSHIP_TYPES, formatDay, optionLabel } from "../lib/format";
import { formatDate, todayISO } from "../../helpers/datetime";
import { Avatar, Badge, Button, Card, Confirm, ErrorBox, Loading, Menu, MenuItem, Select, Tabs } from "../ui";
import { AutoField, DaysInput, DueSummary, InlineName, usePatcher } from "../components/DetailParts";
import Roles from "../components/Roles";
import Timeline from "../components/Timeline";
import History from "../components/History";
import TagEditor from "../components/TagEditor";
import SnoozeMenu from "../components/SnoozeMenu";
import LeadsList from "../components/LeadsList";
import PrivacyCard, { usePrivacyActions } from "../components/PrivacyActions";
import { HiddenBanner } from "../components/Hidden";
import { useShell } from "../shell/context";

function TierHistory({ items }) {
    if (!items || items.length === 0) return null;
    return (
        <Card title="Tier history">
            <div style={{ display: "grid", gap: 4 }}>
                {[...items].reverse().map((h, i) => (
                    <div key={i} className="hz-small">
                        Tier {h.from_tier || "none"} <i className="ri-arrow-right-line hz-muted"></i> {h.to_tier || "none"}
                        <span className="hz-muted"> · {formatDate(h.changed_at)} · {h.source}</span>
                    </div>
                ))}
            </div>
        </Card>
    );
}

export default function ContactDetail() {
    const { id } = useParams();
    const history = useHistory();
    const { version, openLog, openLead, toast, bump } = useShell();
    const [tab, setTab] = useState("touches");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const { data: c, error, loading, reload, setData } = useLoad(() => get(`v1/crm/contacts/${id}`), [id, version]);
    const settings = useLoad(() => get("v1/settings/crm"), []);
    const save = usePatcher(`v1/crm/contacts/${id}`, c || {}, setData);
    const privacy = usePrivacyActions(c || { id, name: "" });
    useDocumentTitle(c ? c.name : "Person");

    if (loading && !c) return <Loading />;
    if (error && !c) return <ErrorBox error={error} onRetry={reload} />;
    if (!c) return null;

    const today = todayISO();
    const tierDefault = c.tier && settings.data ? settings.data[`cadence_days_${c.tier}`] : null;
    const role = c.current_role;
    const snoozed = c.snoozed_until && c.snoozed_until > today;
    const roleChangedRecently = c.role_changed_at && (!c.last_touch_at || c.role_changed_at.slice(0, 10) > c.last_touch_at);

    // E12: hiding only takes the person out of the People list.
    const setHidden = async (hidden) => {
        if (await save({ hidden }, { quiet: true })) {
            toast(hidden ? `${c.name} is hidden from the People list.` : `${c.name} is back in the People list.`, {
                icon: hidden ? "ri-eye-off-line" : "ri-eye-line",
                action: { label: "Undo", run: () => save({ hidden: !hidden }, { quiet: true }) },
            });
        }
    };

    const remove = async () => {
        try {
            await del(`v1/crm/contacts/${id}`);
            setConfirmDelete(false);
            toast(`${c.name} moved to the trash.`, {
                icon: "ri-delete-bin-line",
                action: { label: "Undo", run: () => post(`v1/crm/trash/contacts/${id}/restore`).then(() => { bump(); history.push(`/crm/contacts/${id}`); }).catch((e) => toast(e.message, { error: true })) },
            });
            bump();
            history.push("/crm/contacts");
        } catch (e) {
            setConfirmDelete(false);
            toast(e.message, { error: true });
        }
    };

    return (
        <>
            <Link to={listUrl("/crm/contacts")} className="hz-back"><i className="ri-arrow-left-s-line"></i>People</Link>
            <HiddenBanner kind="contact" entity={c} onUnhide={() => setHidden(false)} />
            <div className="hz-detail-head">
                <Avatar name={c.name} large />
                <div className="hz-detail-id">
                    <InlineName entity={c} save={save} />
                    <div style={{ color: "var(--hz-slate-700)" }}>
                        {role ? (
                            <>
                                {role.title || "Works"} at <Link to={`/crm/companies/${role.company_id}`}>{role.company_name}</Link>
                            </>
                        ) : (
                            "No current role"
                        )}
                        {c.location ? ` · ${c.location}` : ""}
                    </div>
                    <div className="hz-detail-meta">
                        <span className="hz-row" style={{ gap: 6 }}>
                            <span className="hz-small hz-muted">Tier</span>
                            <Select
                                small
                                value={c.tier || ""}
                                onChange={(e) => save({ tier: e.target.value || null })}
                                options={[{ value: "", label: "None" }, { value: "A", label: "A" }, { value: "B", label: "B" }, { value: "C", label: "C" }]}
                                style={{ width: 80 }}
                            />
                        </span>
                        <span className="hz-row" style={{ gap: 6 }}>
                            <span className="hz-small hz-muted">Custom cadence</span>
                            <DaysInput
                                value={c.cadence_days_override}
                                placeholder={tierDefault ? `Tier default (${tierDefault}d)` : "Days"}
                                onSave={(v) => save({ cadence_days_override: v })}
                            />
                        </span>
                        {snoozed && (
                            <Badge color="secondary" pill>
                                <i className="ri-zzz-line"></i> Snoozed until {formatDay(c.snoozed_until, { short: true })}
                            </Badge>
                        )}
                        {roleChangedRecently && (
                            <Badge color="info" title={`Role changed ${formatDate(c.role_changed_at)}`}>Role changed</Badge>
                        )}
                        {c.linkedin_status && <Badge color="secondary">{optionLabel(LINKEDIN_STATUSES, c.linkedin_status)}</Badge>}
                    </div>
                </div>
                <div className="hz-detail-status">
                    <DueSummary entity={c} notTrackedText="Set a tier or a custom cadence to keep in touch." />
                </div>
                <div className="hz-row">
                    <Button variant="primary" icon="ri-chat-new-line" onClick={() => openLog({ contact: { id: c.id, name: c.name, current_role: c.current_role } })}>
                        Log touchpoint
                    </Button>
                    <SnoozeMenu base="contacts" entity={c} onChanged={(res) => { setData((x) => ({ ...x, ...res })); bump(); }} />
                    <Menu trigger={(open, toggle) => <Button variant="outline" iconOnly icon="ri-more-2-fill" title="More" onClick={toggle} style={{ width: 37, height: 37 }} />}>
                        <MenuItem onClick={() => openLead({ contact: { id: c.id, name: c.name, current_role: c.current_role } })}>New lead</MenuItem>
                        {c.linkedin_url && <MenuItem onClick={() => window.open(c.linkedin_url, "_blank", "noopener")}>Open LinkedIn profile</MenuItem>}
                        {!c.hidden_at && <MenuItem onClick={() => setHidden(true)}>Hide from People list</MenuItem>}
                        <MenuItem onClick={privacy.exportData} sub="zip">Export data</MenuItem>
                        <MenuItem danger onClick={() => setConfirmDelete(true)}>Delete person</MenuItem>
                        <MenuItem danger onClick={privacy.openErase} sub="permanent">Erase person…</MenuItem>
                    </Menu>
                </div>
            </div>

            <div className="hz-detail-cols">
                <div className="hz-detail-left">
                    <Card title="Details" actions={<span className="hz-xs hz-muted"><i className="ri-check-line"></i> Saves when you leave a field</span>}>
                        <div className="hz-form-grid">
                            <div className="hz-form-2">
                                <AutoField label="Email" field="email" type="email" entity={c} save={save} />
                                <AutoField label="Phone" field="phone" entity={c} save={save} />
                            </div>
                            <AutoField label="LinkedIn URL" field="linkedin_url" entity={c} save={save} placeholder="linkedin.com/in/…" />
                            <div className="hz-form-2">
                                <AutoField label="Relationship" field="relationship_type" entity={c} save={save} options={RELATIONSHIP_TYPES} />
                                <AutoField label="Location" field="location" entity={c} save={save} />
                            </div>
                            <AutoField label="How we met" field="how_we_met" entity={c} save={save} />
                            <div className="hz-form-2">
                                <AutoField label="Connected on" field="connected_on" type="date" entity={c} save={save} />
                                <Field2 label="Last messaged" value={c.last_messaged_at ? formatDay(c.last_messaged_at) : "Never"} hint="From the LinkedIn messages import. Not a touch." />
                            </div>
                            <AutoField label="Notes" field="notes" entity={c} save={save} multiline />
                        </div>
                    </Card>
                    <Roles contactId={c.id} roles={c.roles || []} onChanged={() => { reload({ silent: true }); bump(); }} />
                    <Card title="Tags">
                        <TagEditor entity="contacts" id={c.id} tags={c.tags || []} onChange={(tags) => { setData((x) => ({ ...x, tags })); bump(); }} />
                    </Card>
                    <LeadsList
                        leads={c.open_leads}
                        showCompany
                        onNew={() => openLead({ contact: { id: c.id, name: c.name, current_role: c.current_role } })}
                        allPath={`/crm/leads?view=list&contact_id=${c.id}&label=${encodeURIComponent(c.name)}`}
                    />
                    <TierHistory items={c.tier_history} />
                    <PrivacyCard actions={privacy} />
                </div>
                <div className="hz-detail-right">
                    <Card
                        noBody
                        title={null}
                    >
                        <Tabs
                            active={tab}
                            onChange={setTab}
                            tabs={[
                                { key: "touches", label: "Touches", icon: "ri-chat-history-line" },
                                { key: "history", label: "History", icon: "ri-history-line" },
                            ]}
                        />
                        {tab === "touches" ? (
                            <>
                                <div className="hz-card-section hz-row" style={{ background: "var(--hz-surface-subtle)" }}>
                                    <span className="hz-small hz-muted hz-spacer">Every real interaction resets the cadence.</span>
                                    <Button size="sm" variant="soft" icon="ri-chat-new-line" onClick={() => openLog({ contact: { id: c.id, name: c.name, current_role: c.current_role } })}>Log a touch</Button>
                                </div>
                                <Timeline path={`v1/crm/contacts/${c.id}/touches`} mentionsPath={`v1/crm/contacts/${c.id}/mentions`} entity={{ id: c.id, name: c.name }} context="contact" version={version} />
                            </>
                        ) : (
                            <History entityType="contact" entityId={c.id} version={version} />
                        )}
                    </Card>
                </div>
            </div>

            {privacy.modal}
            <Confirm open={confirmDelete} title="Delete person" danger confirmLabel="Delete" onConfirm={remove} onClose={() => setConfirmDelete(false)}>
                {c.name} goes to the trash with their roles, touches and tags. Restore it from Settings, Trash within 30 days.
            </Confirm>
        </>
    );
}

function Field2({ label, value, hint }) {
    return (
        <div>
            <span className="hz-label">{label}</span>
            <div style={{ padding: "8px 0" }}>{value}</div>
            {hint && <div className="hz-hint">{hint}</div>}
        </div>
    );
}
